// MypagePage.jsx - マイページの独立ページ
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../utils/axios';
import { loadPatientWebSession } from '../../utils/patientWebAuth';
import {
  ClipboardList,
  Settings,
  MapPin,
  CreditCard,
  Baby,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import PageHeader from './PageHeader';
import '../Mypage/medichat_mypage.scss';

const MypagePage = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // ページ表示時に画面上部にスクロール
    window.scrollTo(0, 0);
    
    // プロフィール情報を取得
    (async () => {
      try {
        const { data } = await axiosInstance.get('/api/medichat/profile');
        setUserProfile(data?.user || {});
      } catch {
        setUserProfile({});
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const fullName = useMemo(() => {
    const last = userProfile?.last_name?.trim() || '';
    const first = userProfile?.first_name?.trim() || '';
    const joined = [last, first].filter(Boolean).join(' ');
    return joined || '—';
  }, [userProfile]);

  const genderText = useMemo(() => {
    return (userProfile?.gender || '').toString() || '—';
  }, [userProfile]);

  const ageText = useMemo(() => {
    const s = userProfile?.birthdate;
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const hasntHadBirthday =
      today.getMonth() < d.getMonth() ||
      (today.getMonth() === d.getMonth() && today.getDate() < d.getDate());
    if (hasntHadBirthday) age -= 1;
    return `${age}歳`;
  }, [userProfile]);

  const handleNewSession = async () => {
    try {
      const { data } = await axiosInstance.post('/api/medichat/sessions', {});
      navigate(`/medichat/chat/${data.session?.id}`);
    } catch (error) {
      console.error('Failed to create new session:', error);
    }
  };

  const go = (path) => () => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <div className="medichat-container">
        <div className="mypage-loading-overlay">
          <div className="mypage-loading">読み込み中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="medichat-container">
      {/* ヘッダー */}
      <PageHeader title="マイページ" />

      {/* コンテナ */}
      <div className="mypage-container" style={{minHeight:'auto', paddingTop: '56px'}}>
        <div className="mypage-content">
          {/* プロフィールセクション */}
          <div className="medichat-sidemenu__header" style={{ flexDirection: 'column', borderRadius: '16px', marginBottom: '16px' }}>
            <div className="medichat-sidemenu__profile">
              <div className="medichat-sidemenu__profile-meta">
                <div className="name">{fullName}</div>
                <div className="sub">
                  {[
                    userProfile?.id ? `User ID: ${userProfile.id}` : null,
                    genderText || null,
                    ageText || null,
                    (userProfile?.prefecture || '').toString().trim() || null,
                  ].filter(Boolean).join('・') || '—'}
                </div>
              </div>
            </div>
          </div>

          {/* 「新しい診察を始める」ボタン */}
          <button onClick={handleNewSession} className="medichat-new-session-btn" style={{ marginBottom: '16px' }}>
            新しい診察を始める
          </button>

          {/* メニューリスト */}
          <div className="medichat-sidemenu__list">
            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/history')}>
              <span className="medichat-sidemenu__icon">
                <ClipboardList size={18} />
              </span>
              <span className="medichat-sidemenu__label">診療履歴</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/settings')}>
              <span className="medichat-sidemenu__icon">
                <Settings size={18} />
              </span>
              <span className="medichat-sidemenu__label">基本情報</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/insurance')}>
              <span className="medichat-sidemenu__icon">
                <CreditCard size={18} />
              </span>
              <span className="medichat-sidemenu__label">資格確認書・医療証</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/documents')}>
              <span className="medichat-sidemenu__icon">
                <ClipboardList size={18} />
              </span>
              <span className="medichat-sidemenu__label">健診/お薬手帳</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/children')}>
              <span className="medichat-sidemenu__icon">
                <Baby size={18} />
              </span>
              <span className="medichat-sidemenu__label">お子さま情報</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/addresses')}>
              <span className="medichat-sidemenu__icon">
                <MapPin size={18} />
              </span>
              <span className="medichat-sidemenu__label">配送先住所</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>

            <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/payments')}>
              <span className="medichat-sidemenu__icon">
                <CreditCard size={18} />
              </span>
              <span className="medichat-sidemenu__label">支払い方法登録</span>
              <span className="medichat-sidemenu__chev">
                <ChevronRight size={16} />
              </span>
            </button>
          </div>

          {loadPatientWebSession() && (
            <div className="medichat-sidemenu__section">
              <button
                className="medichat-sidemenu__item"
                onClick={() => window.location.assign('/medichat/logout')}
              >
                <span className="medichat-sidemenu__icon">
                  <LogOut size={18} />
                </span>
                <span className="medichat-sidemenu__label">ログアウト</span>
                <span className="medichat-sidemenu__chev">
                  <ChevronRight size={16} />
                </span>
              </button>
            </div>
          )}

          {/* 法的情報リンク */}
          <div className="medichat-sidemenu__legal">
            <div className="medichat-sidemenu__legal-row">
              <a href="https://www.notion.so/CureNow-2cba4be0fa7080cd8e3ee5b623afb4a7" target="_blank" rel="noreferrer">利用規約</a>
              <span className="medichat-sidemenu__legal-sep">|</span>
              <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080bc9233f81808fbb4d0" target="_blank" rel="noreferrer">プライバシーポリシー</a>
            </div>
            <div className="medichat-sidemenu__legal-row">
              <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080edb59ec9ef331e9ee9" target="_blank" rel="noreferrer">キャンセルポリシー</a>
              <span className="medichat-sidemenu__legal-sep">|</span>
              <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa708045a472fab1f4069ba1" target="_blank" rel="noreferrer">特定商取引法に基づく表示</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MypagePage;
